/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiClient } from '../services/api';
import type { AuthUser, LoginRequest, RegisterRequest } from '../types';

interface AuthContextType {
  user: AuthUser | null;
  permissions: string[];
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  switchOrganization: (targetTenantId: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(() => !!localStorage.getItem('auth_token'));

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return;
    }

    apiClient
      .validateToken()
      .then((validatedUser) => {
        setUser(validatedUser);
        setPermissions(validatedUser.permissions ?? []);
      })
      .catch(() => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        setUser(null);
        setPermissions([]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = async (credentials: LoginRequest) => {
    const response = await apiClient.login(credentials);
    localStorage.setItem('auth_token', response.token);
    if (response.refreshToken) {
      localStorage.setItem('refresh_token', response.refreshToken);
    }
    const validatedUser = await apiClient.validateToken();
    setUser(validatedUser);
    setPermissions(validatedUser.permissions ?? []);
  };

  const register = async (data: RegisterRequest) => {
    const response = await apiClient.register(data);
    localStorage.setItem('auth_token', response.token);
    const validatedUser = await apiClient.validateToken();
    setUser(validatedUser);
    setPermissions(validatedUser.permissions ?? []);
  };

  const switchOrganization = async (targetTenantId: string) => {
    throw new Error(`Organization switching is not supported in the Admin Portal. tenant=${targetTenantId}`);
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setPermissions([]);
  };

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      permissions,
      isLoading,
      isAuthenticated: !!user,
      login,
      register,
      switchOrganization,
      logout,
    }),
    [isLoading, permissions, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
